/**
 * Frontend-only UX persona presets (Epic 22). Not backend roles.
 */
export type PersonaPresetId =
  | 'viewer'
  | 'live_ops'
  | 'setup_reader'
  | 'setup_writer'
  | 'acceptance_manager'
  | 'access_admin'
  | 'super_admin'
  | 'external_reviewer'
  | 'product_evaluator'
  | 'sandbox_ehs_evaluator';

export type PersonaPreset = {
  id: PersonaPresetId;
  label: string;
  description: string;
  capabilities: string[];
  defaultLandingPath?: string;
  primaryProfile?: 'operations' | 'setup' | 'acceptance' | 'admin';
  secondaryProfiles?: Array<'operations' | 'setup' | 'acceptance' | 'admin'>;
};

export const PERSONA_PRESETS: PersonaPreset[] = [
  {
    id: 'viewer',
    label: 'Viewer',
    description: 'Minimal capabilities; overview and globally visible surfaces only',
    capabilities: [],
    defaultLandingPath: '/acceptance/overview',
  },
  {
    id: 'live_ops',
    label: 'Live Ops',
    description: 'Incidents queue and audit review',
    capabilities: ['incidents:read', 'audit:read'],
    defaultLandingPath: '/incidents',
    primaryProfile: 'operations',
  },
  {
    id: 'setup_reader',
    label: 'Setup Reader',
    description: 'Read-only setup and rollout surfaces',
    capabilities: ['devices:read', 'deployments:read', 'cameras:read', 'policies:read', 'blueprints:read', 'sites:read'],
    defaultLandingPath: '/setup-rollout',
    primaryProfile: 'setup',
  },
  {
    id: 'setup_writer',
    label: 'Setup Writer',
    description: 'Setup reader plus rollout write actions',
    capabilities: [
      'devices:read',
      'devices:write',
      'deployments:read',
      'deployments:write',
      'cameras:read',
      'cameras:write',
      'policies:read',
      'policies:write',
      'policies:deploy',
      'blueprints:read',
      'sites:read',
    ],
    defaultLandingPath: '/setup-rollout',
    primaryProfile: 'setup',
  },
  {
    id: 'acceptance_manager',
    label: 'Acceptance Manager',
    description: 'Incident context plus setup write for proof, readiness, and handoff work',
    capabilities: [
      'incidents:read',
      'devices:read',
      'devices:write',
      'deployments:read',
      'deployments:write',
      'policies:read',
      'policies:deploy',
      'audit:read',
    ],
    defaultLandingPath: '/acceptance/overview',
    primaryProfile: 'acceptance',
    secondaryProfiles: ['operations', 'setup'],
  },
  {
    id: 'access_admin',
    label: 'Access Admin',
    description: 'User administration only',
    capabilities: ['admin:users:manage'],
    defaultLandingPath: '/users',
    primaryProfile: 'admin',
  },
  {
    id: 'super_admin',
    label: 'Super Admin',
    description: 'Broad capability superset for demos (not a magic backend profile)',
    capabilities: [
      'admin:*',
      'admin:users:manage',
      'admin:rbac:manage',
      'incidents:read',
      'incidents:write',
      'audit:read',
      'audit:write',
      'devices:read',
      'devices:write',
      'deployments:read',
      'deployments:write',
      'cameras:read',
      'cameras:write',
      'policies:read',
      'policies:write',
      'policies:deploy',
      'blueprints:read',
      'blueprints:write',
      'sites:read',
      'sites:write',
      'metrics:read',
    ],
    defaultLandingPath: '/acceptance/overview',
  },
  {
    id: 'external_reviewer',
    label: 'External Reviewer',
    description: 'Selected-review Signed Safety Scan review with external-safe package access (dev preview only)',
    capabilities: [
      'safety_case:view_selected',
      'signed_scan:view_selected',
      'evidence:view_summary',
      'evidence:view_representative',
      'evidence:redacted_only',
      'package:download_external_safe',
      'audit_trace:view',
    ],
    defaultLandingPath: '/review',
  },
  {
    id: 'product_evaluator',
    label: 'Product Evaluator',
    description:
      'Read-only evaluation surfaces: Live View, Cameras, Zones, Policies, activity, Safety Cases, and Safety Reports',
    capabilities: [
      'site:view',
      'camera:view',
      'live_view:view',
      'zone:view',
      'policy:view',
      'safety_case:view_selected',
      'signed_scan:view_selected',
      'evidence:view_summary',
      'evidence:view_representative',
      'evidence:redacted_only',
      'package:download_external_safe',
      'audit_trace:view',
      'incidents:read',
    ],
    defaultLandingPath: '/live-view',
    primaryProfile: 'operations',
  },
  {
    id: 'sandbox_ehs_evaluator',
    label: 'WorkSafeBC Sandbox EHS',
    description:
      'Bounded demo/ws1 sandbox: feed, zone, policy, Safety Cases, corrective actions, and Safety Scan — no admin or live ops',
    capabilities: [
      'site:view',
      'camera:view',
      'live_view:view',
      'zone:view',
      'zone:manage',
      'policy:view',
      'policy:manage',
      'safety_case:view',
      'safety_case:create',
      'safety_case:update',
      'corrective_action:view',
      'corrective_action:create',
      'corrective_action:update',
      'signed_scan:view',
      'signed_scan:sign',
      'evidence:view_summary',
      'evidence:view_representative',
      'package:download_external_safe',
      'audit_trace:view',
      'incidents:read',
    ],
    defaultLandingPath: '/live-view',
    primaryProfile: 'operations',
  },
];

export function getPersonaPresetById(id: string | null | undefined): PersonaPreset | null {
  if (!id) return null;
  return PERSONA_PRESETS.find((p) => p.id === id) || null;
}

export function isValidPersonaPresetId(id: string): boolean {
  return PERSONA_PRESETS.some((p) => p.id === id);
}
